import type { MaskShape, OcclusionStudyMode } from '../../../sdk/@types/Revisao';
import { studentMaskFace } from './occlusionStudy';

/** Five frame-measured, theme-invariant colours, indexed by position — never stored (design, Decisão 2). */
export const MASK_COLORS = ['maskc-1', 'maskc-2', 'maskc-3', 'maskc-4', 'maskc-5'] as const;

export type CorMarcacao = (typeof MASK_COLORS)[number];

/**
 * Reordering recolours on purpose: the badge's number and colour move together.
 * Returns the bare token key, an identity — not a class fragment to interpolate
 * (`` `bg-${cor}` `` never reaches Tailwind's scanner). `classesDaMarcacao` below
 * is that literal `Record<CorMarcacao, string>`, built once at the point of use —
 * don't build a second one; read that instead.
 */
export function corDaMarcacao(indice: number): CorMarcacao {
  return MASK_COLORS[indice % MASK_COLORS.length];
}

/**
 * Literal Tailwind classes for each of the five tokens, built once so the
 * editor's canvas and the panel's row paint from the exact same map instead
 * of each inventing its own (design, Decisão 2/3 — one rule, two readers).
 * Written out in full on purpose: a template literal (`` `bg-maskc-${n}` ``)
 * never reaches Tailwind's scanner (see `corDaMarcacao` above).
 *
 * Values measured off flashcard.pen (e4tQc dark / Urykw light — identical
 * node-for-node): the unselected rectangle fills its own hue at 75% opacity
 * (`#..bf`) with a solid border of the same hue; selected, the fill rises to
 * 90% (`#..e6`) and the border turns white — same hue, fuller treatment,
 * never a sixth colour (design, Decisão 2; spec "O editor de oclusão ordena
 * as marcações…", scenario "A pré-visualização mostra o mesmo destaque").
 * The on-canvas badge is a white circle with the number in the mask's own
 * hue; the panel row's badge inverts that (solid hue, white number) to read
 * at a glance in a list, and its trailing "next" row previews the colour the
 * next mark will take, outlined rather than filled, so it reads as a
 * preview rather than a claim on a mask that doesn't exist yet.
 */
export interface ClassesDaMarcacao {
  /** Rectangle border + fill, not selected. */
  retangulo: string;
  /** Rectangle border + fill, selected — same hue, fuller fill, white ring. */
  retanguloSelecionado: string;
  /** The on-canvas circular badge's number colour (badge itself is white). */
  numeroBadge: string;
  /** The panel row's badge: this colour as solid fill, white number. */
  linhaBadge: string;
  /** The panel's trailing empty row — outline preview of the next colour. */
  linhaBadgeProxima: string;
}

const CLASSES_POR_COR: Record<CorMarcacao, ClassesDaMarcacao> = {
  'maskc-1': {
    retangulo: 'border-maskc-1 bg-maskc-1/[0.75]',
    retanguloSelecionado: 'border-white bg-maskc-1/[0.9]',
    numeroBadge: 'text-maskc-1',
    linhaBadge: 'bg-maskc-1 text-white',
    linhaBadgeProxima: 'border border-maskc-1 bg-maskc-1/[0.2] text-maskc-1',
  },
  'maskc-2': {
    retangulo: 'border-maskc-2 bg-maskc-2/[0.75]',
    retanguloSelecionado: 'border-white bg-maskc-2/[0.9]',
    numeroBadge: 'text-maskc-2',
    linhaBadge: 'bg-maskc-2 text-white',
    linhaBadgeProxima: 'border border-maskc-2 bg-maskc-2/[0.2] text-maskc-2',
  },
  'maskc-3': {
    retangulo: 'border-maskc-3 bg-maskc-3/[0.75]',
    retanguloSelecionado: 'border-white bg-maskc-3/[0.9]',
    numeroBadge: 'text-maskc-3',
    linhaBadge: 'bg-maskc-3 text-white',
    linhaBadgeProxima: 'border border-maskc-3 bg-maskc-3/[0.2] text-maskc-3',
  },
  'maskc-4': {
    retangulo: 'border-maskc-4 bg-maskc-4/[0.75]',
    retanguloSelecionado: 'border-white bg-maskc-4/[0.9]',
    numeroBadge: 'text-maskc-4',
    linhaBadge: 'bg-maskc-4 text-white',
    linhaBadgeProxima: 'border border-maskc-4 bg-maskc-4/[0.2] text-maskc-4',
  },
  'maskc-5': {
    retangulo: 'border-maskc-5 bg-maskc-5/[0.75]',
    retanguloSelecionado: 'border-white bg-maskc-5/[0.9]',
    numeroBadge: 'text-maskc-5',
    linhaBadge: 'bg-maskc-5 text-white',
    linhaBadgeProxima: 'border border-maskc-5 bg-maskc-5/[0.2] text-maskc-5',
  },
};

/**
 * The one place that turns a mask's position into paintable classes.
 * `OcclusionEditor`'s canvas and `MarcacoesPanel`'s row both call this —
 * neither should build its own `Record<CorMarcacao, string>`.
 */
export function classesDaMarcacao(indice: number): ClassesDaMarcacao {
  return CLASSES_POR_COR[corDaMarcacao(indice)];
}

/**
 * Border-radius class for how a marking's box is painted — the only thing `shape`
 * decides (design, Decisão 4: a elipse é recorte, não geometria nova — "border-radius:
 * 50% para a elipse", literally). The box's `x/y/w/h` stays whatever drives
 * hit-testing, the six handles and the live measurement; this never feeds into any of
 * that geometry. Absent `shape` is `rect`, so a marking saved before the field existed
 * keeps rendering exactly as authored. `OclusaoFace` (student face) and
 * `OcclusionEditor` (canvas + both previews) both read this instead of each
 * hardcoding its own radius choice.
 *
 * `rounded-[50%]`, not `rounded-full`: Tailwind's `full` is a fixed `9999px` on both
 * axes of every corner, and the CSS corner-overlap algorithm scales all eight radius
 * components by one shared factor, preserving each corner's horizontal:vertical ratio
 * (1:1, since both started at 9999px) — on a non-square box that clamps to the
 * *shorter* side on both axes, producing a stadium/pill (straight edges on the long
 * sides) instead of an ellipse. A percentage resolves horizontal and vertical
 * independently against the box's own width and height, which is what actually
 * inscribes an ellipse — confirmed live: `rounded-full` measured as a visible
 * straight-edged pill on this component's wide/short boxes before this fix.
 */
export function classeDaForma(shape: MaskShape | undefined): string {
  return shape === 'ellipse' ? 'rounded-[50%]' : 'rounded';
}

export type AparenciaMarcacao =
  | 'neutra'
  | 'perguntada-coberta'
  | 'perguntada-revelada'
  | 'rotulada'
  | 'editor';

/** 'edicao' is the editor's own canvas; 'estudo' is anyone simulating the student — the real face or the editor's preview. */
export type ContextoAparencia = 'edicao' | 'estudo';

/**
 * The one rule for how a marking renders, read by the student face and by the editor's
 * preview alike (design, Decisão 3). `perguntada` is the asked index — `studentMaskFace`'s
 * own `asked` — so pass the same number for every marking on one card.
 */
export function aparenciaDaMarcacao(
  face: 'front' | 'back',
  modo: OcclusionStudyMode,
  indice: number,
  perguntada: number,
  contexto: ContextoAparencia,
): AparenciaMarcacao {
  if (contexto === 'edicao') return 'editor';
  const estado = studentMaskFace(face, modo, indice, perguntada);
  const ehPerguntada = indice === perguntada;
  if (estado === 'covered') return ehPerguntada ? 'perguntada-coberta' : 'neutra';
  return ehPerguntada ? 'perguntada-revelada' : 'rotulada';
}

/** '?' marks only the one actually being asked — every other covered mark shows its number. */
export function textoDaMarcacao(papel: AparenciaMarcacao, indice: number, rotulo: string): string {
  if (papel === 'perguntada-coberta') return '?';
  if (papel === 'perguntada-revelada' || papel === 'rotulada') return rotulo;
  return String(indice + 1);
}

/** rotation 200deg, measured off flashcard.pen (nodes dqIA1/sGsgj) via GetVariables. */
export const GRADIENTE_PERGUNTADA =
  'linear-gradient(200deg, rgb(var(--c-ask-from)) 0%, rgb(var(--c-ask-to)) 100%)';

/**
 * One entry per `AparenciaMarcacao`. The student face (`OclusaoFace`, in the review
 * session) and the editor's own "Aluno vê frente/verso" preview both paint a mask from
 * this one map — neither keeps a local copy — so retouching a treatment moves both
 * screens in the same step (design, Decisão 3). `editor` never reaches either reader:
 * `OcclusionEditor`'s edit-mode canvas paints from `classesDaMarcacao` above instead.
 */
export const CLASSES_DA_APARENCIA: Record<AparenciaMarcacao, string> = {
  neutra: 'border-mask bg-mask text-mask-text',
  'perguntada-coberta':
    'border-transparent text-white ring-[3px] ring-ask-ring/[0.502] md:ring-ask-ring/40',
  'perguntada-revelada':
    'border-transparent bg-reveal text-white ring-[3px] ring-reveal-ring/[0.349]',
  rotulada: 'border-edge bg-surface text-ink',
  editor: '',
};
